import React, { useEffect, useMemo, useState } from 'react';
import {
  useAdminCategories,
  useCreateAdminCategory,
  useUpdateAdminCategory,
  useDeleteAdminCategory,
  ExpenseCategory,
} from '../../../services/adminCategories';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Stack,
  Skeleton,
} from '@mui/material';
import LinearProgress from '@mui/material/LinearProgress';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useForm, Controller } from 'react-hook-form';

 type CategoryFormValues = {
  name: string;
  description?: string | null;
  code?: string; // create only
  is_active: boolean;
  requires_receipt: boolean;
  max_amount?: number | null;
};

export function CategoriesSettingsPage() {
  const [page, setPage] = useState(0); // zero-based for MUI
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 500); // increase debounce to reduce request churn
    return () => clearTimeout(t);
  }, [search]);
  const [includeInactive, setIncludeInactive] = useState(true);

  const queryParams = useMemo(
    () => ({ page: page + 1, size: rowsPerPage, search: debouncedSearch, include_inactive: includeInactive }),
    [page, rowsPerPage, debouncedSearch, includeInactive]
  );
  const { data, isLoading, isError, error, refetch, isFetching } = useAdminCategories(queryParams) as any;
  // Only show busy indicators after a short delay to avoid flashing spinners on fast responses
  const [showSearchBusy, setShowSearchBusy] = useState(false);
  useEffect(() => {
    let timer: any;
    if (isFetching) {
      timer = setTimeout(() => setShowSearchBusy(true), 200);
    } else {
      setShowSearchBusy(false);
    }
    return () => timer && clearTimeout(timer);
  }, [isFetching]);

  const createMutation = useCreateAdminCategory();
  const updateMutation = useUpdateAdminCategory();
  const deleteMutation = useDeleteAdminCategory();
  const isCreating = createMutation.isLoading;
  const isUpdating = updateMutation.isLoading;

  // Create dialog state
  const [openCreate, setOpenCreate] = useState(false);

  // Edit dialog state
  const [openEdit, setOpenEdit] = useState(false);
  const [editing, setEditing] = useState<ExpenseCategory | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    control,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm<CategoryFormValues>({
    defaultValues: {
      name: '',
      description: '',
      code: '',
      is_active: true,
      requires_receipt: true,
      max_amount: undefined,
    },
  });

  const openCreateDialog = () => {
    reset({ name: '', description: '', code: '', is_active: true, requires_receipt: true, max_amount: undefined });
    setOpenCreate(true);
  };

  const openEditDialog = (cat: ExpenseCategory) => {
    setEditing(cat);
    reset({
      name: cat.name,
      description: cat.description ?? '',
      is_active: cat.is_active,
      requires_receipt: cat.requires_receipt,
      max_amount: cat.max_amount ? Number(cat.max_amount) : undefined,
    });
    setOpenEdit(true);
  };

  const onCreate = handleSubmit(async (values) => {
    // Normalize inputs
    const name = (values.name || '').trim();
    const code = (values.code || '').trim().toUpperCase();
    const description = (values.description || '').trim();

    // Client-side uniqueness pre-check against current page data for immediate UX
    const existingItems: ExpenseCategory[] = (data?.items as ExpenseCategory[]) || [];
    const nameClash = existingItems.some((c: ExpenseCategory) => (c.name || '').trim().toLowerCase() === name.toLowerCase());
    const codeClash = existingItems.some((c: ExpenseCategory) => (c.code || '').trim().toUpperCase() === code);
    if (nameClash) {
      // Prevent submit and show inline error
      setError('name', { type: 'manual', message: 'Name already exists' });
      return;
    }
    if (!code) {
      setError('code', { type: 'manual', message: 'Code is required' });
      return;
    }
    if (codeClash) {
      setError('code', { type: 'manual', message: 'Code already exists' });
      return;
    }

    try {
      await createMutation.mutateAsync({
        name,
        description: description || undefined,
        code,
        is_active: values.is_active,
        requires_receipt: values.requires_receipt,
        max_amount: values.max_amount ?? undefined,
      });
      clearErrors();
      setOpenCreate(false);
      refetch();
    } catch (e: any) {
      // Gracefully handle conflicts and validation errors
      const status = e?.response?.status;
      const detail: string | undefined = e?.response?.data?.detail || e?.message;
      if (status === 409) {
        // Backend signals uniqueness conflict (code or possibly name)
        const msg = (detail || '').toLowerCase();
        if (msg.includes('code')) {
          setError('code', { type: 'server', message: 'Code already exists' });
        } else if (msg.includes('name')) {
          setError('name', { type: 'server', message: 'Name already exists' });
        } else {
          setError('code', { type: 'server', message: 'Category already exists' });
        }
        // Keep dialog open; do not throw
        return;
      }
      // For other errors, show a generic inline message on name to avoid noisy toasts
      setError('name', { type: 'server', message: 'Could not create category. Please try again.' });
      return;
    }
  });

  const onUpdate = handleSubmit(async (values) => {
    if (!editing) return;
    // Normalize and pre-check name uniqueness (code is immutable)
    const name = (values.name || '').trim();
    const description = (values.description || '').trim();
    const existingItems: ExpenseCategory[] = (data?.items as ExpenseCategory[]) || [];
    const nameClash = existingItems.some((c: ExpenseCategory) => c.id !== editing.id && (c.name || '').trim().toLowerCase() === name.toLowerCase());
    if (nameClash) {
      setError('name', { type: 'manual', message: 'Name already exists' });
      return;
    }

    try {
      await updateMutation.mutateAsync({
        id: editing.id,
        payload: {
          name,
          description: description || undefined,
          is_active: values.is_active,
          requires_receipt: values.requires_receipt,
          max_amount: values.max_amount ?? undefined,
        },
      });
      setOpenEdit(false);
      setEditing(null);
      clearErrors();
      refetch();
    } catch (e: any) {
      const status = e?.response?.status;
      const detail: string | undefined = e?.response?.data?.detail || e?.message;
      if (status === 409) {
        const msg = (detail || '').toLowerCase();
        if (msg.includes('name')) {
          setError('name', { type: 'server', message: 'Name already exists' });
        } else {
          setError('name', { type: 'server', message: 'Conflict updating category' });
        }
        return;
      }
      setError('name', { type: 'server', message: 'Could not update category. Please try again.' });
      return;
    }
  });

  const onDelete = async (cat: ExpenseCategory) => {
    await deleteMutation.mutateAsync({ id: cat.id });
    refetch();
  };

  return (
    <Box p={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Categories</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateDialog}>
          New Category
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" mb={1}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
          size="small"
          // Keep input enabled; only show subtle busy indicators
          helperText={showSearchBusy ? 'Searching…' : ' '}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                {showSearchBusy ? <CircularProgress size={18} /> : null}
              </InputAdornment>
            ),
          }}
        />
        <FormControlLabel
          control={<Switch checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />}
          label="Include inactive"
        />
      </Stack>

      {(isLoading || showSearchBusy) && <LinearProgress sx={{ mb: 2 }} />}

      {isError && (
        <Typography color="error" variant="body2" mb={2}>
          {(error as any)?.message || 'Failed to load categories'}
        </Typography>
      )}

      <Box sx={{ position: 'relative' }}>
        {showSearchBusy && !isLoading && (
          <Box sx={{ position: 'absolute', top: 8, right: 16, zIndex: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        <Table size="small" sx={{ tableLayout: 'fixed', opacity: showSearchBusy && !isLoading ? 0.6 : 1 }}>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Code</TableCell>
            <TableCell>Description</TableCell>
            <TableCell align="center">Active</TableCell>
            <TableCell align="center">Req. Receipt</TableCell>
            <TableCell align="right">Max Amount</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* Loading skeleton rows to keep layout stable */}
          {isLoading &&
            Array.from({ length: 6 }).map((_, i) => (
              <TableRow key={`skeleton-${i}`}>
                <TableCell><Skeleton width="60%" /></TableCell>
                <TableCell><Skeleton width="40%" /></TableCell>
                <TableCell><Skeleton width="80%" /></TableCell>
                <TableCell align="center"><Skeleton width={24} /></TableCell>
                <TableCell align="center"><Skeleton width={24} /></TableCell>
                <TableCell align="right"><Skeleton width={40} /></TableCell>
                <TableCell align="right"><Skeleton width={80} /></TableCell>
              </TableRow>
            ))}
          {data?.items?.map((cat: ExpenseCategory) => (
            <TableRow key={cat.id} hover>
              <TableCell>{cat.name}</TableCell>
              <TableCell>{cat.code}</TableCell>
              <TableCell>{cat.description}</TableCell>
              <TableCell align="center">{cat.is_active ? 'Yes' : 'No'}</TableCell>
              <TableCell align="center">{cat.requires_receipt ? 'Yes' : 'No'}</TableCell>
              <TableCell align="right">{cat.max_amount ?? '-'}</TableCell>
              <TableCell align="right">
                <IconButton size="small" onClick={() => openEditDialog(cat)} aria-label="Edit">
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton size="small" onClick={() => onDelete(cat)} aria-label="Delete">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
          {!isLoading && (data?.items?.length ?? 0) === 0 && (
            <TableRow>
              <TableCell colSpan={7} align="center">
                <Typography variant="body2">No categories found</Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
        </Table>
      </Box>

      <Box sx={{ opacity: isFetching ? 0.6 : 1, pointerEvents: isFetching ? 'none' : 'auto' }}>
        <TablePagination
        component="div"
        count={data?.total ?? 0}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 20, 50]}
        />
      </Box>

      {/* Create Dialog */}
      <Dialog
        open={openCreate}
        onClose={() => setOpenCreate(false)}
        fullWidth
        maxWidth="sm"
        container={() => document.getElementById('root') as HTMLElement}
      >
        <DialogTitle>New Category</DialogTitle>
        <DialogContent>
          {isCreating && <LinearProgress />}
          <Box component="form" onSubmit={onCreate} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              {...register('name', { required: 'Name is required' })}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={isCreating}
            />
            <TextField
              label="Code"
              fullWidth
              margin="normal"
              {...register('code', { required: 'Code is required' })}
              error={!!errors.code}
              helperText={errors.code?.message}
              disabled={isCreating}
            />
            <TextField label="Description" fullWidth margin="normal" {...register('description')} disabled={isCreating} />
            <Stack direction="row" spacing={2} mt={1}>
              <FormControlLabel control={<Switch defaultChecked disabled={isCreating} {...register('is_active')} />} label="Active" />
              <FormControlLabel control={<Switch defaultChecked disabled={isCreating} {...register('requires_receipt')} />} label="Requires receipt" />
            </Stack>
            <TextField
              label="Max Amount"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', min: '0' }}
              {...register('max_amount', { valueAsNumber: true, min: { value: 0, message: 'Must be >= 0' } })}
              disabled={isCreating}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreate(false)}>Cancel</Button>
          <Button onClick={onCreate} variant="contained" disabled={isCreating}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={openEdit}
        onClose={() => setOpenEdit(false)}
        fullWidth
        maxWidth="sm"
        container={() => document.getElementById('root') as HTMLElement}
      >
        <DialogTitle>Edit Category</DialogTitle>
        <DialogContent>
          {isUpdating && <LinearProgress />}
          <Box component="form" onSubmit={onUpdate} sx={{ mt: 1 }}>
            <TextField
              label="Name"
              fullWidth
              margin="normal"
              {...register('name', { required: 'Name is required' })}
              error={!!errors.name}
              helperText={errors.name?.message}
              disabled={isUpdating}
            />
            {/* Code is immutable in update */}
            <TextField label="Code" fullWidth margin="normal" value={editing?.code || ''} disabled />
            <TextField label="Description" fullWidth margin="normal" {...register('description')} disabled={isUpdating} />
            <Stack direction="row" spacing={2} mt={1}>
              <Controller
                name="is_active"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={isUpdating} />}
                    label="Active"
                  />
                )}
              />
              <Controller
                name="requires_receipt"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={<Switch checked={!!field.value} onChange={(e) => field.onChange(e.target.checked)} disabled={isUpdating} />}
                    label="Requires receipt"
                  />
                )}
              />
            </Stack>
            <TextField
              label="Max Amount"
              type="number"
              fullWidth
              margin="normal"
              inputProps={{ step: '0.01', min: '0' }}
              {...register('max_amount', { valueAsNumber: true, min: { value: 0, message: 'Must be >= 0' } })}
              disabled={isUpdating}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenEdit(false)}>Cancel</Button>
          <Button onClick={onUpdate} variant="contained" disabled={isUpdating}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default CategoriesSettingsPage;
